import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const StyledLink = styled.a`
  padding: 1.2rem 2.5rem;
  font-size: 1.2rem;
  background-color: ${props => props.accent ? '#FF9800' : '#4CAF50'};
  color: white;
  text-decoration: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Lato', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    background-color: ${props => props.accent ? '#F57C00' : '#45a049'};
    transform: translateY(-2px);
  }
`;

const HeroSection = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
              url('${props => props.$backgroundImage}') center/cover no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  position: relative;
  box-sizing: border-box;
`;

const SlideControls = styled.div`
  position: absolute;
  inset: auto 1.25rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const SlideButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-family: 'Lato', sans-serif;
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.55);
  }
`;

const SlideCount = styled.span`
  font-family: 'Lato', sans-serif;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
  min-width: 62px;
`;

const MainHeading = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Playfair Display', serif;
`;

const SubHeading = styled.h2`
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  margin-bottom: 2rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  max-width: 800px;
`;

const Section = styled.section`
  padding: 5rem 2rem;
  background-color: ${props => props.background || 'white'};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: ${props => props.marginBottom || '3rem'};
  color: #333;
  font-family: 'Playfair Display', serif;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #4CAF50;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
`;

const ServiceCard = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: left;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  h3 {
    color: #333;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-family: 'Playfair Display', serif;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background-color: #4CAF50;
    }
  }

  p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-family: 'Lato', sans-serif;
  }
`;

const AboutSection = styled(Section)`
  background-color: #f9f9f9;
`;

const AboutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
  
  p {
    color: #666;
    line-height: 1.8;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    font-family: 'Lato', sans-serif;
  }
`;

const CTASection = styled(Section)`
  background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
              url('/images/cta-engineering.jpg') center/cover no-repeat;
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;

  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    font-family: 'Playfair Display', serif;
    color: white;
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    color: #f0f0f0;
    line-height: 1.8;
  }
`;

const HomePage = () => {
  const slideCandidates = useMemo(() => {
    const extensions = ['jpg', 'jpeg', 'png', 'webp']

    return Array.from({ length: 6 }, (_, index) => {
      const base = `/slideshow/slide${index + 1}`
      return extensions.map(ext => `${base}.${ext}`)
    })
  }, [])

  const [slideImages, setSlideImages] = useState([])
  const [activeSlide, setActiveSlide] = useState(0)
  const activeSlidesRef = useRef([])
  const intervalRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    const loadImage = src =>
      new Promise(resolve => {
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = src
      })

    const resolveSlides = async () => {
      const resolved = []

      for (const variants of slideCandidates) {
        let found = ''
        for (const src of variants) {
          // eslint-disable-next-line no-await-in-loop
          const ok = await loadImage(src)
          if (ok) {
            found = src
            break
          }
        }
        if (found) resolved.push(found)
      }

      if (cancelled) return

      activeSlidesRef.current = resolved
      setSlideImages(resolved)
      setActiveSlide(0)
    }

    resolveSlides()

    return () => {
      cancelled = true
    }
  }, [slideCandidates])

  const resetAutoAdvance = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    const slides = activeSlidesRef.current
    if (!slides.length) return

    intervalRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides.length)
    }, 6000)
  }

  useEffect(() => {
    if (!slideImages.length) return
    resetAutoAdvance()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    // Intentionally re-run when slideImages becomes available
  }, [slideImages])

  const handlePreviousSlide = () => {
    if (!slideImages.length) return
    setActiveSlide(prev => (prev - 1 + slideImages.length) % slideImages.length)
    resetAutoAdvance()
  }

  const handleNextSlide = () => {
    if (!slideImages.length) return
    setActiveSlide(prev => (prev + 1) % slideImages.length)
    resetAutoAdvance()
  }

  const heroBackground = slideImages.length ? slideImages[activeSlide] : '/images/hero.png'

  return (
    <>
      <HeroSection $backgroundImage={heroBackground}>
        <MainHeading>We harness cutting-edge technologies</MainHeading>
        <SubHeading>to empower local enterprises</SubHeading>
        <StyledLink href="/contact">Contact Us</StyledLink>

        {slideImages.length ? (
          <SlideControls>
          <SlideButton type="button" onClick={handlePreviousSlide} aria-label="Previous slide">
            Previous
          </SlideButton>
          <SlideCount>{activeSlide + 1}/{slideImages.length}</SlideCount>
          <SlideButton type="button" onClick={handleNextSlide} aria-label="Next slide">
            Next
          </SlideButton>
          </SlideControls>
        ) : null}
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle>Our Services</SectionTitle>
          <Grid>
            <ServiceCard>
              <h3>GSM Installation and Commissioning</h3>
              <p>We deliver complete installation and comissining services for GSM communication systems, ensuring robust, reliable, and high quality network connectivity essential for modern communication needs.</p>
            </ServiceCard>
            <ServiceCard>
              <h3>Power Systems Engineering</h3>
              <p>Our team specializes in designing and implementing hybrid and solar power solutions meticulously tailored to the unique every requirements of our clients, delivering automatic power systems known for their exceptional reliability and energy efficiency.</p>
            </ServiceCard>
            <ServiceCard>
              <h3>Information Technology (IT) Infrastructure</h3>
              <p>We provide comprehensive services in developing and managing secure IT networks, including local area networks (LAN), wide area networks (WAN), and server systems, while deploying scalable IT solutions that significantly enhance operational efficiency for our clients.</p>
            </ServiceCard>
          </Grid>
        </Container>
      </Section>

      <AboutSection>
        <Container>
          <SectionTitle>About Sunbold</SectionTitle>
          <AboutContent>
            <p>Sun Bold Inc. is a proudly Liberian-owned engineering firm committed to delivering innovative, reliable, and sustainable solutions across a wide spectrum of sectors, including GSM communication, automatic power systems, network infrastructure, construction and technical installation and repair services.</p>
            <p>Founded on March 20, 2014, our firm has established itself as a trusted partner in Liberia's developmental undertakings. We are dedicated to the reconstruction and enhancement of the nation's infrastructure, delivering high-quality engineering services and technologies that address the dynamic needs of Liberian enterprises while fostering economic growth and resilience within our communities.</p>
          </AboutContent>
        </Container>
      </AboutSection>

      <CTASection>
        <Container>
          <CTAContent>
            <h2>Ready to Transform Your Ideas into Reality?</h2>
            <p>Let's discuss how Sunbold can help you achieve your engineering goals.</p>
            <StyledLink href="/contact" accent>Schedule a Consultation</StyledLink>
          </CTAContent>
        </Container>
      </CTASection>
    </>
  );
};

export default HomePage;
